import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Plus, X, FileDown, Camera, Truck, ArrowDownToLine, ArrowUpFromLine, Pencil, Trash2 } from "lucide-react";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import EmployeeStatusFilter from "@/components/EmployeeStatusFilter";
import EmployeePicker from "@/components/EmployeePicker";
import VehiclePicker from "@/components/VehiclePicker";
import SignatureCanvas from "@/components/SignatureCanvas";
import { usePermissions } from "@/contexts/PermissionsContext";
import ModuleHint from "@/components/ModuleHint";
import { fmtDate } from "@/lib/format";
import { EmptyState } from "@/components/Shared";
import { compressImage, fileToDataUrl, fmtBytes } from "@/lib/imageCompressor";

export default function Uebergaben() {
  const { can } = usePermissions();
  const canCreate = can("fuhrpark", "create");
  const canEdit = can("fuhrpark", "edit");
  const canDelete = can("fuhrpark", "delete");

  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  // Filters
  const [deptFilter, setDeptFilter] = useDepartmentFilter("uebergaben");
  const [empStatus, setEmpStatus] = useState("aktiv");
  const [direction, setDirection] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (direction) params.direction = direction;
      if (vehicleFilter) params.vehicle_id = vehicleFilter;
      if (empFilter) params.employee_id = empFilter;
      if (deptFilter) params.department_id = deptFilter;
      const { data } = await api.get("/handovers", { params });
      let filtered = data;
      if (dateFrom) filtered = filtered.filter(h => h.date >= dateFrom);
      if (dateTo) filtered = filtered.filter(h => h.date <= dateTo);
      setItems(filtered);
    } catch (e) { toast.error("Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [direction, vehicleFilter, empFilter, deptFilter, dateFrom, dateTo]);
  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/employees", { params: empStatus ? { status: empStatus } : {} }).then(({ data }) => setEmployees(data));
  }, [empStatus]);

  const empName = (id) => {
    const e = employees.find(x => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : "?";
  };
  const vehLabel = (id) => vehicles.find(x => x.id === id)?.license_plate || "?";

  async function downloadPdf(h) {
    try {
      const res = await api.get(`/handovers/${h.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `uebergabe-${h.date}-${vehLabel(h.vehicle_id)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF-Download fehlgeschlagen"); }
  }

  async function deleteHandover(h) {
    if (!window.confirm(`Eintrag vom ${h.date} (${vehLabel(h.vehicle_id)}) wirklich löschen?`)) return;
    try {
      await api.delete(`/handovers/${h.id}`);
      toast.success("Eintrag gelöscht");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  }

  // Stats
  const stats = useMemo(() => {
    const u = items.filter(h => h.direction === "uebergabe").length;
    const r = items.filter(h => h.direction === "ruecknahme").length;
    return { total: items.length, u, r };
  }, [items]);

  return (
    <div className="p-6 md:p-8" data-testid="uebergaben-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-zinc-500"/> KFZ Übergaben / Rücknahmen
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {stats.total} Einträge · <span className="text-emerald-400">{stats.u}</span> Übergaben · <span className="text-amber-400">{stats.r}</span> Rücknahmen
          </p>
        </div>
        {canCreate && (
          <button onClick={() => setShowForm(true)} data-testid="ueb-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded">
            <Plus className="w-4 h-4"/> Neue hinzufügen
          </button>
        )}
      </div>

      <ModuleHint moduleKey="uebergaben" />

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-3 mb-4 flex flex-wrap gap-2 items-center">
        <FirmFilter compact />
        <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="uebergaben" testid="ueb-dept-filter"/>
        <EmployeeStatusFilter value={empStatus} onChange={setEmpStatus} testid="ueb-empstatus-filter" size="sm"/>
        <select value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="ueb-direction-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100">
          <option value="">Alle Typen</option>
          <option value="uebergabe">Nur Übergaben</option>
          <option value="ruecknahme">Nur Rücknahmen</option>
        </select>
        <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)} data-testid="ueb-vehicle-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 max-w-[180px]">
          <option value="">Alle Fahrzeuge</option>
          {vehicles.map(v => <option key={v.id} value={v.id}>{v.license_plate}</option>)}
        </select>
        <select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)} data-testid="ueb-emp-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 max-w-[200px]">
          <option value="">Alle Fahrer</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="ueb-date-from"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="ueb-date-to"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Laden…</div>
        ) : items.length === 0 ? (
          <EmptyState label="Keine Übergaben/Rücknahmen gefunden." />
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Datum</th>
              <th className="px-4 py-3 text-left">Typ</th>
              <th className="px-4 py-3 text-left">Fahrzeug</th>
              <th className="px-4 py-3 text-left">Fahrer</th>
              <th className="px-4 py-3 text-right">KM</th>
              <th className="px-4 py-3 text-right">Tank %</th>
              <th className="px-4 py-3 text-center">CL</th>
              <th className="px-4 py-3 text-center">📷</th>
              <th className="px-4 py-3 text-center">✍</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(h => {
              const isU = h.direction === "uebergabe";
              const cl = h.checklist_answers || [];
              const cl_ok = cl.filter(x => x.value).length;
              return (
                <tr key={h.id} data-testid={`ueb-row-${h.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5 text-zinc-300 font-mono">{fmtDate(h.date)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${isU ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border border-amber-500/30"}`}>
                      {isU ? <ArrowDownToLine className="w-3 h-3"/> : <ArrowUpFromLine className="w-3 h-3"/>}
                      {isU ? "Übergabe" : "Rücknahme"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-200 font-medium">{vehLabel(h.vehicle_id)}</td>
                  <td className="px-4 py-2.5 text-zinc-300">{empName(h.employee_id)}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">{(h.km || 0).toLocaleString("de-AT")}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-400">{h.fuel_percent ?? "—"}%</td>
                  <td className="px-4 py-2.5 text-center text-[10px] font-mono">
                    {cl.length > 0 ? <span className={cl_ok === cl.length ? "text-emerald-400" : "text-amber-400"}>{cl_ok}/{cl.length}</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-[11px]">{(h.photos?.length || 0) > 0 ? <span className="text-blue-400 font-mono">{h.photos.length}</span> : <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-2.5 text-center">{h.signature_data_url ? <span className="text-emerald-400">✓</span> : <span className="text-zinc-600">—</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => downloadPdf(h)} data-testid={`ueb-pdf-${h.id}`}
                        title="PDF herunterladen"
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                        <FileDown className="w-3.5 h-3.5"/> PDF
                      </button>
                      {canEdit && (
                        <button onClick={() => { setEditing(h); setShowForm(true); }} data-testid={`ueb-edit-${h.id}`}
                          title="Bearbeiten"
                          className="inline-flex items-center text-zinc-400 hover:text-zinc-100">
                          <Pencil className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => deleteHandover(h)} data-testid={`ueb-delete-${h.id}`}
                          title="Löschen"
                          className="inline-flex items-center text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>

      {showForm && (
        <UebergabeForm
          vehicles={vehicles}
          employees={employees}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// UebergabeForm — full handover form with checklist + signature + photos
// ============================================================
function UebergabeForm({ vehicles, employees, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(() => editing ? {
    direction: editing.direction || "uebergabe",
    date: editing.date || today,
    employee_id: editing.employee_id || "",
    vehicle_id: editing.vehicle_id || "",
    km: editing.km || 0,
    fuel_percent: editing.fuel_percent ?? 100,
    notes: editing.notes || "",
    signature_data_url: editing.signature_data_url || "",
    photos: editing.photos || [],
    checklist_answers: editing.checklist_answers || [],
  } : {
    direction: "uebergabe",
    date: today,
    employee_id: "",
    vehicle_id: "",
    km: 0,
    fuel_percent: 100,
    notes: "",
    signature_data_url: "",
    photos: [],
    checklist_answers: [],
  });
  const [checklists, setChecklists] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastKmHint, setLastKmHint] = useState(null);

  // Load checklists once
  useEffect(() => {
    api.get("/checklists").then(({ data }) => {
      setChecklists(data || []);
    }).catch(() => setChecklists([]));
  }, []);

  // Whenever direction changes OR checklists are loaded, populate answers
  // (but never overwrite existing answers when editing an existing handover).
  useEffect(() => {
    if (!checklists || checklists.length === 0) return;
    if (isEdit && form.checklist_answers && form.checklist_answers.length > 0) return;
    const cl = checklists.find(c => c.category === form.direction);
    if (cl && (cl.items || []).length > 0) {
      setForm(f => ({
        ...f,
        checklist_answers: cl.items.map(it => ({
          label: it.label, value: false, notes: "",
        })),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.direction, checklists]);

  // KM-plausibility hint
  useEffect(() => {
    if (!form.vehicle_id) { setLastKmHint(null); return; }
    api.get(`/handovers/last-km`, { params: { vehicle_id: form.vehicle_id } }).then(({ data }) => {
      setLastKmHint(typeof data?.last_km === "number" ? data.last_km : null);
    }).catch(() => setLastKmHint(null));
  }, [form.vehicle_id]);

  function toggleAnswer(i, value) {
    setForm(f => ({ ...f, checklist_answers: f.checklist_answers.map((a, idx) => idx === i ? { ...a, value } : a) }));
  }
  function setAnswerNotes(i, notes) {
    setForm(f => ({ ...f, checklist_answers: f.checklist_answers.map((a, idx) => idx === i ? { ...a, notes } : a) }));
  }

  async function handlePhotos(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const photos = [];
    let savedBytes = 0;
    for (const f of files) {
      if (f.size > 20 * 1024 * 1024) { toast.error(`${f.name} > 20 MB`); continue; }
      // Phase 5Z.42 — compress to ≤1920px before storing as dataURL
      const compressed = await compressImage(f, { maxDimension: 1920, jpegQuality: 0.85 });
      savedBytes += Math.max(0, f.size - compressed.size);
      const dataUrl = await fileToDataUrl(compressed);
      photos.push(dataUrl);
    }
    if (savedBytes > 50 * 1024) toast.info(`Bilder komprimiert: ${fmtBytes(savedBytes)} gespart`);
    setForm(f => ({ ...f, photos: [...f.photos, ...photos] }));
  }

  function removePhoto(i) {
    setForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    if (!form.employee_id) { toast.error("Fahrer pflicht"); return; }
    if (!form.vehicle_id) { toast.error("Fahrzeug pflicht"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/handovers/${editing.id}`, form);
        toast.success("Eintrag aktualisiert");
      } else {
        await api.post("/handovers", form);
        toast.success(form.direction === "uebergabe" ? "Übergabe gespeichert" : "Rücknahme gespeichert");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  }

  const kmWarning = lastKmHint !== null && form.km > 0 && form.km < lastKmHint;
  const checklistRedCount = form.checklist_answers.filter(a => !a.value).length;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="ueb-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Truck className="w-4 h-4 text-blue-400"/>
            {isEdit
              ? (form.direction === "uebergabe" ? "Übergabe bearbeiten" : "Rücknahme bearbeiten")
              : (form.direction === "uebergabe" ? "Neue Fahrzeug-Übergabe" : "Neue Fahrzeug-Rücknahme")}
          </h3>
          <button onClick={onClose} className="text-zinc-400" data-testid="ueb-form-close"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Header fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Typ *</label>
              <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}
                data-testid="ueb-direction"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="uebergabe">Übergabe (Fahrer erhält Fahrzeug)</option>
                <option value="ruecknahme">Rücknahme (Fahrer gibt Fahrzeug zurück)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Datum *</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="ueb-date"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Fahrer *</label>
              <div className="mt-1">
                <EmployeePicker value={form.employee_id} testid="ueb-emp-picker"
                  onChange={(v) => setForm({ ...form, employee_id: v })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Fahrzeug *</label>
              <div className="mt-1">
                <VehiclePicker value={form.vehicle_id} testid="ueb-veh-picker"
                  onChange={(v) => setForm({ ...form, vehicle_id: v })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">KM *</label>
              <input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: +e.target.value })}
                data-testid="ueb-km"
                className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${kmWarning ? "border-red-500/60 ring-1 ring-red-500/30" : "border-zinc-800"}`}/>
              {lastKmHint !== null && (
                <div className={`text-[10px] mt-1 ${kmWarning ? "text-red-400 font-semibold" : "text-zinc-500"}`}>
                  Letzter KM-Stand: <span className="font-mono">{lastKmHint.toLocaleString("de-AT")}</span>
                  {kmWarning && " ⚠️ NEUER KM-STAND IST KLEINER!"}
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Tankfüllung %</label>
              <input type="number" min={0} max={100} value={form.fuel_percent} onChange={(e) => setForm({ ...form, fuel_percent: +e.target.value })}
                data-testid="ueb-fuel"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
          </div>

          {/* Checklist */}
          {form.checklist_answers.length > 0 && (
            <div data-testid="ueb-checklist">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
                  Sicherheits-Checkliste · {form.checklist_answers.length - checklistRedCount}/{form.checklist_answers.length} OK
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setForm({ ...form, checklist_answers: form.checklist_answers.map(a => ({ ...a, value: true })) })}
                    data-testid="ueb-checklist-allyes"
                    className="text-[10px] px-2 py-0.5 bg-emerald-600/20 text-emerald-300 border border-emerald-500/30 rounded hover:bg-emerald-600/40">Alle ✓</button>
                  <button onClick={() => setForm({ ...form, checklist_answers: form.checklist_answers.map(a => ({ ...a, value: false })) })}
                    className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 border border-zinc-700 rounded hover:bg-zinc-700">Zurücksetzen</button>
                </div>
              </div>
              {checklistRedCount > 0 && (
                <div className="mb-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px] text-amber-300">
                  ⚠️ <span className="font-semibold">{checklistRedCount} Punkt(e) nicht erledigt</span> — bitte sorgfältig durchgehen oder Notiz hinterlegen.
                </div>
              )}
              <div className="space-y-1.5">
                {form.checklist_answers.map((a, i) => (
                  <div key={a.id || a.label || i} data-testid={`ueb-cl-row-${i}`}
                    className={`flex items-start gap-3 p-2 rounded border ${a.value ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/15"}`}>
                    <button type="button" onClick={() => toggleAnswer(i, !a.value)}
                      data-testid={`ueb-cl-toggle-${i}`}
                      role="switch"
                      aria-checked={a.value}
                      className={`shrink-0 mt-0.5 inline-flex items-center h-5 w-9 rounded-full p-0.5 transition-colors ${a.value ? "bg-emerald-500" : "bg-zinc-700"}`}>
                      <span className={`block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform ${a.value ? "translate-x-4" : "translate-x-0"}`}/>
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-100">{i + 1}. {a.label}</div>
                      {!a.value && (
                        <input value={a.notes || ""} onChange={(e) => setAnswerNotes(i, e.target.value)}
                          placeholder="Anmerkung (optional)"
                          className="w-full mt-1 px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-[11px] text-zinc-300"/>
                      )}
                    </div>
                    <span className={`shrink-0 text-[10px] font-bold ${a.value ? "text-emerald-400" : "text-red-400"}`}>{a.value ? "JA" : "NEIN"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photos */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1.5">Schadensfotos / Zustand ({form.photos.length})</label>
            <input type="file" multiple accept="image/*" onChange={handlePhotos} data-testid="ueb-photos-upload"
              className="text-xs text-zinc-400 file:mr-2 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-blue-600 file:text-white"/>
            {form.photos.length > 0 && (
              <div className="mt-2 grid grid-cols-4 gap-2">
                {form.photos.map((p, i) => (
                  <div key={`${typeof p === "string" ? p.slice(-32) : i}-${i}`} className="relative group">
                    <img src={p} alt="" className="w-full h-20 object-cover rounded border border-zinc-800"/>
                    <button onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-600/80 hover:bg-red-600 text-white rounded-full text-xs opacity-0 group-hover:opacity-100">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1">Anmerkung</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="ueb-notes"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>

          {/* Signature */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1.5">Unterschrift Fahrer</label>
            <SignatureCanvas value={form.signature_data_url} onChange={(url) => setForm({ ...form, signature_data_url: url })} testid="ueb-signature"/>
          </div>
        </div>

        <div className="p-5 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="ueb-save-btn"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
